import { useState, useEffect, useContext, lazy, Suspense } from "react";

// react-router components
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Dashboard 2 React components
import Sidenav from "ui/Sidenav";
import Configurator from "ui/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeDark from "assets/theme-dark";

// Material Dashboard 2 React routes
import {
  adminRoutes,
  routes,
  broadwayRm,
  storeTeam,
  sisVendor,
  termsheetRoute,
  userRoleManagementRoute,
  newProductRoute,
} from "routes";

// Material Dashboard 2 React contexts
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
  NotificationProvider,
  SearchProvider,
  FilterProvider,
  AuthContext,
  FormDirtyProvider,
} from "context";

// Images
import braodwayLogo from "./assets/images/broadwayLogo.svg";

// Custom Page Components
// import LandingPage from "layouts/landing";
// import Register from "auth/register";
import ProtectedRoute from "ui/ProtectedRoute";
import Login from "auth/login";
import Loader from "components/MDLoader";
import { BULK_LIVE_DATE_UPDATE_USERS } from "layouts/termsheet-master/bulkLiveDateUsers";
import { isManagementUser, isUnifiedAdmin, userHasRole } from "auth/roles";

const ForgotPassword = lazy(() => import("auth/forgot-password"));
const ResetPassword = lazy(() => import("auth/reset-password"));
const UserProfile = lazy(() => import("layouts/user-profile"));
const UserManagement = lazy(() => import("layouts/user-management"));

// Custom Services
import { setupAxiosInterceptors } from "./services/interceptor";

export default function App() {
  const authContext = useContext(AuthContext);

  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, direction, layout, openConfigurator, sidenavColor, transparentSidenav, whiteSidenav, darkMode } =
    controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();

  const [isDemo, setIsDemo] = useState(false);

  useEffect(() => {
    setIsDemo(process.env.REACT_APP_IS_DEMO === "true");
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // if the token expired or other errors it logs out and goes to the login page
  const navigate = useNavigate();
  setupAxiosInterceptors(() => {
    authContext.logout();
    navigate("/auth/login");
  });

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route && route.type !== "auth") {
        return (
          <Route
            exact
            path={route.route}
            element={<ProtectedRoute isAuthenticated={authContext.isAuthenticated}>{route.component}</ProtectedRoute>}
            key={route.key}
          />
        );
      }
      return null;
    });

  const getUserRoutes = () => {
    const getUserRoutesByRole = () => {
      switch (authContext.user?.role) {
        case "admin":
          return adminRoutes;
        case "ops_manager":
        case "business_manager":
        case "commercial_manager":
        case "mdm_manager":
        case "broadway-rm":
          return broadwayRm;
        case "brand_admin":
          return routes;
        case "sis-vendor":
          return sisVendor;
        case "store-team":
          return storeTeam;
        case "unified-admin":
          return [];
        default:
          return [];
      }
    };

    const addTermsheetMaster = (routeList) => {
      if (
        authContext.user?.isFinance ||
        authContext.user?.isManagement ||
        BULK_LIVE_DATE_UPDATE_USERS.includes(authContext.user?.username)
      ) {
        return [termsheetRoute, ...routeList];
      }
      return routeList;
    };

    const addUserRoleManagement = (routeList) =>
      isManagementUser(authContext.user)
        ? [...routeList, userRoleManagementRoute]
        : routeList;

    const insertRouteAfter = (routeList, afterKey, route) => {
      const index = routeList.findIndex((item) => item.key === afterKey);
      if (index === -1) {
        return [...routeList, route];
      }
      return [
        ...routeList.slice(0, index + 1),
        route,
        ...routeList.slice(index + 1),
      ];
    };

    const addNewProduct = (routeList) =>
      isUnifiedAdmin(authContext.user)
        ? insertRouteAfter(routeList, "product", newProductRoute)
        : routeList;

    return addNewProduct(
      addUserRoleManagement(addTermsheetMaster(getUserRoutesByRole())),
    );
  };

  return (
    <>
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />
        <NotificationProvider>
          <FilterProvider>
            <SearchProvider>
              <FormDirtyProvider>
                {layout === "dashboard" && pathname !== "/" && pathname !== "/home" && (
                  <>
                    <Sidenav
                      color={sidenavColor}
                      brand={(transparentSidenav && !darkMode) || whiteSidenav ? braodwayLogo : braodwayLogo}
                      brandName="Broadway"
                      routes={getUserRoutes()}
                      onMouseEnter={handleOnMouseEnter}
                      onMouseLeave={handleOnMouseLeave}
                    >
                      {/* We can paas addition child here pathak */}
                    </Sidenav>
                    <Configurator />
                  </>
                )}
                {layout === "vr" && <Configurator />}
                <Suspense fallback={<Loader />}>
                <Routes>
                  <Route path="/" element={<Login />} />
                  <Route path="/auth/login" element={<Login />} />
                  {/* TODO: we dont allow public to register directly on our portal for now */}
                  {/* <Route path="/auth/register" element={<Register />} /> */}
                  <Route path="/auth/forgot-password" element={<ForgotPassword />} />
                  <Route path="/auth/reset-password" element={<ResetPassword />} />
                  <Route
                    exact
                    path="user-profile"
                    element={
                      <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
                        <UserProfile />
                      </ProtectedRoute>
                    }
                    key="user-profile"
                  />
                  <Route
                    exact
                    path="user-management"
                    element={
                      <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
                        <UserManagement />
                      </ProtectedRoute>
                    }
                    key="user-management"
                  />
                  {userHasRole(authContext.user, "brand_admin") && (
                    <Route
                      path="/reports"
                      element={<Navigate to="/dashboard" replace />}
                      key="reports-brand-block"
                    />
                  )}
                  {getRoutes(getUserRoutes())}
                  <Route path="*" element={<Navigate to="/" />} />
                </Routes>
                </Suspense>
              </FormDirtyProvider>
            </SearchProvider>
          </FilterProvider>
        </NotificationProvider>
      </ThemeProvider>
    </>
  );
}
